import React, { useState, useEffect } from 'react';
import './App.css';
import { questions } from './questions';
import axios from 'axios';
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';

function App() {
  const [currentStep, setCurrentStep] = useState('welcome'); // welcome, questions, review, result
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [generatedPRD, setGeneratedPRD] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  // Load saved progress from localStorage
  useEffect(() => {
    const savedAnswers = localStorage.getItem('prdAnswers');
    if (savedAnswers) {
      setAnswers(JSON.parse(savedAnswers));
    }
  }, []);

  // Save progress to localStorage whenever answers change
  useEffect(() => {
    localStorage.setItem('prdAnswers', JSON.stringify(answers));
  }, [answers]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers({
      ...answers,
      [questionId]: value
    });
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setCurrentStep('review');
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleEditQuestion = (index) => {
    setCurrentQuestionIndex(index);
    setCurrentStep('questions');
  };

  const generatePRD = async () => {
    setIsGenerating(true);
    setError('');

    const prompt = `You are an expert Product Manager from a MAANG company. Based on the following answers to a PRD questionnaire, create a comprehensive, professional Product Requirements Document following Google/Amazon standards.

Format the PRD with clear sections, professional language, and proper structure. Make it detailed and actionable.

Here are the answers:

1. Executive Summary: ${answers[1] || 'Not provided'}

2. Problem Statement: ${answers[2] || 'Not provided'}

3. Goals and Success Metrics: ${answers[3] || 'Not provided'}

4. Target Users: ${answers[4] || 'Not provided'}

5. User Stories: ${answers[5] || 'Not provided'}

6. Scope: ${answers[6] || 'Not provided'}

7. Functional Requirements: ${answers[7] || 'Not provided'}

8. User Experience: ${answers[8] || 'Not provided'}

9. Technical Requirements: ${answers[9] || 'Not provided'}

10. Timeline: ${answers[10] || 'Not provided'}

11. Risks and Mitigations: ${answers[11] || 'Not provided'}

12. Supporting Information: ${answers[12] || 'Not provided'}

Please create a professional PRD with these sections:
- Executive Summary
- Problem Statement
- Goals and Success Metrics
- Target Users
- User Stories and Use Cases
- Scope (In Scope / Out of Scope)
- Functional Requirements
- User Experience Requirements
- Technical Requirements
- Timeline and Milestones
- Risks and Mitigation Strategies
- Appendix (Supporting Information)

Use professional formatting with headers, bullet points where appropriate, and clear structure.`;

    try {
      const response = await axios.post('https://prd-backend-ot8j.onrender.com/api/generate-prd', {
        prompt: prompt
      });

      const prdText = response.data.content[0].text;
      setGeneratedPRD(prdText);
      setCurrentStep('result');
    } catch (err) {
      console.error('Error generating PRD:', err);
      setError('Failed to generate PRD. Please make sure the server is running and try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadAsWord = async () => {
    const doc = new Document({
      sections: [{
        properties: {},
        children: generatedPRD.split('\n').map(line => {
          if (line.startsWith('# ')) {
            return new Paragraph({
              text: line.replace('# ', ''),
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 }
            });
          } else if (line.startsWith('## ')) {
            return new Paragraph({
              text: line.replace('## ', ''),
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 300, after: 150 }
            });
          } else if (line.startsWith('### ')) {
            return new Paragraph({
              text: line.replace('### ', ''),
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 }
            });
          } else if (line.trim() === '') {
            return new Paragraph({ text: '' });
          } else {
            return new Paragraph({
              children: [new TextRun(line)],
              spacing: { after: 100 }
            });
          }
        })
      }]
    });

    const blob = await Packer.toBlob(doc);
    const date = new Date().toISOString().split('T')[0];
    saveAs(blob, `PRD_${date}.docx`);
  };

  const startNewPRD = () => {
    setAnswers({});
    setCurrentQuestionIndex(0);
    setCurrentStep('welcome');
    setGeneratedPRD('');
    localStorage.removeItem('prdAnswers');
  };

  // WELCOME SCREEN
  if (currentStep === 'welcome') {
    return (
      <div className="App">
        <div className="welcome-container">
          <h1>PRD Assistant</h1>
          <p className="subtitle">Create professional Product Requirements Documents in 10 minutes</p>
          <div className="welcome-content">
            <p>This tool will guide you through 12 essential questions to create a comprehensive PRD based on MAANG company best practices.</p>
            <ul className="features-list">
              <li>✓ Structured interview process</li>
              <li>✓ AI-powered PRD generation</li>
              <li>✓ Download as Word document</li>
              <li>✓ Auto-save your progress</li>
            </ul>
            <button className="btn-primary" onClick={() => setCurrentStep('questions')}>
              Get Started
            </button>
          </div>
        </div>
      </div>
    );
  }

  // QUESTIONS SCREEN
  if (currentStep === 'questions') {
    const currentQuestion = questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

    return (
      <div className="App">
        <div className="questions-container">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
          <div className="progress-text">
            Question {currentQuestionIndex + 1} of {questions.length} ({Math.round(progress)}%)
          </div>

          <div className="question-card">
            <div className="section-label">{currentQuestion.section}</div>
            <h2>{currentQuestion.question}</h2>
            
            <textarea
              className="answer-input"
              value={answers[currentQuestion.id] || ''}
              onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
              placeholder={currentQuestion.placeholder}
              rows={8}
            />
            
            <div className="char-count">
              {(answers[currentQuestion.id] || '').length} characters
            </div>

            <div className="tip-box">
              <strong>💡 Tip:</strong> {currentQuestion.tip}
            </div>

            <div className="button-group">
              <button 
                className="btn-secondary" 
                onClick={handleBack}
                disabled={currentQuestionIndex === 0}
              >
                ← Back
              </button>
              <button 
                className="btn-primary" 
                onClick={handleNext}
              >
                {currentQuestionIndex === questions.length - 1 ? 'Review Answers' : 'Next →'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // REVIEW SCREEN
  if (currentStep === 'review') {
    return (
      <div className="App">
        <div className="review-container">
          <h1>Review Your Answers</h1>
          <p className="subtitle">Check your answers before generating the PRD</p>

          <div className="answers-list">
            {questions.map((q, index) => (
              <div key={q.id} className="answer-item">
                <div className="answer-header">
                  <div>
                    <div className="section-label">{q.section}</div>
                    <h3>{q.question}</h3>
                  </div>
                  <button 
                    className="btn-edit" 
                    onClick={() => handleEditQuestion(index)}
                  >
                    Edit
                  </button>
                </div>
                <div className="answer-text">
                  {answers[q.id] || <em>Not answered</em>}
                </div>
              </div>
            ))}
          </div>

          <div className="button-group">
            <button 
              className="btn-secondary" 
              onClick={() => setCurrentStep('questions')}
            >
              ← Back to Questions
            </button>
            <button 
              className="btn-primary btn-generate" 
              onClick={generatePRD}
              disabled={isGenerating}
            >
              {isGenerating ? 'Generating PRD...' : 'Generate PRD'}
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}
        </div>
      </div>
    );
  }

  // RESULT SCREEN
  if (currentStep === 'result') {
    return (
      <div className="App">
        <div className="result-container">
          <h1>Your PRD is Ready!</h1>
          
          <div className="button-group">
            <button className="btn-primary" onClick={downloadAsWord}>
              📄 Download as Word
            </button>
            <button className="btn-secondary" onClick={startNewPRD}>
              Create New PRD
            </button>
          </div>

          <div className="prd-display">
            <pre>{generatedPRD}</pre>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default App;
